import * as React from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { ArrowLeft, ArrowUpRight, Compass } from 'lucide-react';
import { MeshBackground } from '../../design-system/components/MeshBackground';
import { NoiseOverlay } from '../../design-system/components/NoiseOverlay';
import { WorqlyMark } from '../../design-system/components/WorqlyMark';
import { MagneticButton } from '../../design-system/components/MagneticButton';
import { VISUAL } from '../../design-system/visual';

/**
 * Editorial 404. Two large numerals that tilt independently with the cursor
 * (the "4" goes one way, the "0" the other, the "4" the same as the first),
 * plus a serif-italic accent strapline. Marketing-side context so we keep
 * the editorial typography.
 */
export function NotFoundPage() {
  return (
    <div className="relative min-h-[100dvh] flex flex-col bg-background text-foreground overflow-hidden">
      <MeshBackground />
      <NoiseOverlay />

      <header className="relative z-10 sticky top-0">
        <div className="absolute inset-0 -z-10 backdrop-blur-xl bg-background/70 border-b border-border/40" />
        <nav className="max-w-5xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" aria-label="Worqly home">
            <WorqlyMark size="md" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to worqly.io
          </Link>
        </nav>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-10">
        <div className="max-w-2xl w-full text-center">
          <p className={[VISUAL.eyebrow, 'mb-6'].join(' ')}>Lost in transit</p>

          <KineticNumerals />

          <h1 className="font-serif italic text-foreground text-balance text-[clamp(2rem,4.5vw,2.75rem)] leading-[1.05] mt-8">
            That page took a detour.
          </h1>
          <p className="mt-4 text-[14.5px] leading-relaxed text-muted-foreground max-w-lg mx-auto">
            We couldn't find what you're after. It may have been moved, renamed, or the link's
            stale. Use the compass below — most of the action lives on the dashboard or the
            landing page.
          </p>

          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <MagneticButton strength={0.18}>
              <Link to="/" className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-6 h-11 text-[14px] font-semibold text-background shadow-[0_8px_24px_-10px_oklch(0_0_0/0.35)] ring-1 ring-foreground/10">
                <Compass className="h-3.5 w-3.5" />
                Go home
              </Link>
            </MagneticButton>
            <MagneticButton strength={0.12}>
              <Link to="/login" className="inline-flex items-center gap-1.5 rounded-full bg-card/80 ring-1 ring-border/60 px-5 h-11 text-[14px] font-semibold text-foreground hover:ring-border/90 transition-colors">
                Sign in
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </MagneticButton>
          </div>

          <p className="mt-12 text-[12px] text-muted-foreground/70 font-serif italic">
            If you typed the URL by hand, double-check it — small letters tend to drift.
          </p>
        </div>
      </main>
    </div>
  );
}

/**
 * Two large numerals that tilt with cursor parallax — each digit lives in
 * its own MagneticTilt with its own polarity so the "4-0-4" wobbles slightly
 * as the user moves their mouse. Serif italic for editorial.
 */
function KineticNumerals() {
  return (
    <div className="relative inline-flex items-center justify-center gap-2 sm:gap-4">
      <NumeralTilt polarity={1}>4</NumeralTilt>
      <NumeralOrbit />
      <NumeralTilt polarity={-1}>4</NumeralTilt>
    </div>
  );
}

function NumeralTilt({ children, polarity }: { children: React.ReactNode; polarity: 1 | -1 }) {
  const ref = React.useRef<HTMLSpanElement>(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-200, 200], [polarity * 8, polarity * -8]), { stiffness: 90, damping: 16 });
  const ry = useSpring(useTransform(mx, [-200, 200], [polarity * -10, polarity * 10]), { stiffness: 90, damping: 16 });
  const x  = useSpring(useTransform(mx, [-200, 200], [polarity * -8, polarity * 8]), { stiffness: 80, damping: 18 });

  React.useEffect(() => {
    function onMove(e: MouseEvent) {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      mx.set(e.clientX - (r.left + r.width / 2));
      my.set(e.clientY - (r.top + r.height / 2));
    }
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [mx, my]);

  return (
    <motion.span
      ref={ref}
      style={{ rotateX: rx, rotateY: ry, x, transformPerspective: 900 }}
      className="font-serif italic text-foreground leading-none text-[clamp(6rem,18vw,11rem)] tracking-[-0.04em] will-change-transform"
      aria-hidden
    >
      {children}
    </motion.span>
  );
}

/**
 * The middle "0" — a saffron ring with an inner serif italic 0 that rotates
 * lazily. Implies the missing page is in some kind of orbit / void.
 */
function NumeralOrbit() {
  return (
    <motion.span
      className="relative inline-flex items-center justify-center"
      style={{ width: 'clamp(6rem,18vw,11rem)', height: 'clamp(6rem,18vw,11rem)' }}
      aria-hidden
    >
      <motion.span
        className="absolute inset-[12%] rounded-full ring-[3px] ring-saffron/60"
        animate={{ rotate: 360 }}
        transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
        style={{ borderTopColor: 'transparent', borderRightColor: 'transparent' }}
      />
      <motion.span
        className="absolute inset-[18%] rounded-full ring-1 ring-saffron/30"
        animate={{ scale: [1, 1.06, 1], opacity: [0.7, 1, 0.7] }}
        transition={{ duration: 3.6, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.span
        animate={{ rotate: -360 }}
        transition={{ duration: 42, repeat: Infinity, ease: 'linear' }}
        className="font-serif italic text-foreground leading-none text-[clamp(5rem,14vw,8.5rem)] tracking-[-0.04em] will-change-transform"
      >
        0
      </motion.span>
    </motion.span>
  );
}
